/**
 * Compress an image file on the client before upload.
 * Resizes to maxWidth (preserving aspect ratio) and re-encodes as JPEG.
 * 5MB phone photos shrink to ~200-400KB without visible quality loss.
 */
export async function compressImage(file, maxWidth = 1600, quality = 0.85) {
  if (!file || !file.type?.startsWith("image/")) return file;
  // Don't compress GIFs (animated) — would lose animation
  if (file.type === "image/gif") return file;

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round(height * (maxWidth / width));
        width = maxWidth;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          // If compressed is somehow larger than original, keep original
          if (blob.size >= file.size) { resolve(file); return; }
          const compressed = new File(
            [blob],
            file.name.replace(/\.\w+$/, ".jpg"),
            { type: "image/jpeg", lastModified: Date.now() }
          );
          resolve(compressed);
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}
